import cn from 'classnames';
import { createPortal } from 'react-dom';
import { getImage } from '../../../utilities/iconResolver';
import { t } from '../../../utilities/translator';
import PromptHints from './PromptHints';
import FadeTransition from '../../../components/animations/FadeTransition';
import AudioRecorder from './AudioRecorder';
import useAIAssistant from './useAIAssistant';
import { useDispatch } from 'react-redux';
import { isMobile } from '../../../utilities/helpers';
import { setCurrentStep } from '../onboardingSlice';

const AIAssistant = () => {
  const dispatch = useDispatch();
  const {
    prompt,
    isRecording,
    setIsRecording,
    handleSetPrompt,
    handleSendAudio,
    handleStartConversationClick,
    handleFeedBuilderClick,
    isAudioLoading,
    error,
    setError,
    MAX_PROMPT_LENGTH,
  } = useAIAssistant();

  const startConversation = () => {
    dispatch(setCurrentStep(1));
    handleStartConversationClick();
  }

  const footerPortal = document.getElementById('wizard-next-button');

  if (!isMobile()) {
    // Desktop wizard AI step
    return (
      <div className="wizard-ai-step">
        <div className="wizard-step ai-assistant ai-assistant-desktop flex flex-col gap-8">
          <div className="text-center mb-auto">
            <div className="ai-title flex items-center gap-3 justify-center"><span>{t('onboarding.assistant.title')}</span><i className="ri-bard-line" /></div>
          </div>
          <FadeTransition in={prompt.length === 0} unmountOnExit>
            <PromptHints onSelect={handleSetPrompt} />
          </FadeTransition>
          <div className="relative">
            {!isRecording && (
              <>
                <textarea
                  name="prompt"
                  placeholder={t('onboarding.assistant.placeholder')}
                  className={cn('ai-textarea', { loading: isAudioLoading, expanded: prompt.length > 0 })}
                  value={prompt}
                  onChange={(e) => handleSetPrompt(e.target.value)}
                  maxLength={MAX_PROMPT_LENGTH}
                />
                <div className={cn('prompt-length', { hidden: prompt.length === 0 })}>
                  <span>{prompt.length}/{MAX_PROMPT_LENGTH}</span>
                </div>
              </>
            )}

            <AudioRecorder
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              onFinishRecording={handleSendAudio}
              showLoader={isAudioLoading}
              setError={setError}
            />
          </div>
        </div>
        {footerPortal && createPortal(
          <button
            className="wizard-btn wizard-btn--next wizard-btn--ai-desktop"
            onClick={startConversation}
            disabled={prompt.length < 3 || isAudioLoading || isRecording}
          >
            {t('onboarding.assistant.generateFeedBtn')}
          </button>,
          footerPortal
        )}
      </div>
    );
  }

  return (
  <div className="wizard-layout">
    <div className="wizard-navbar">
      <div className="wizard-navigation flex justify-center">
        <img src={getImage('HF-white-Logo.svg')} alt="" />
      </div>
    </div>
    <div className="wizard-step ai-assistant flex flex-col gap-6">
      <div className="text-start mb-auto">
        <div className="ai-title">{t('onboarding.assistant.title')}</div>
        <div className="mt-3 ai-subtitle">{t('onboarding.assistant.subtitle')}</div>
      </div>
      <FadeTransition in={prompt.length === 0} unmountOnExit>
        <PromptHints onSelect={handleSetPrompt} />
      </FadeTransition>
      <div className="relative">
        {!isRecording && (
          <>
            <textarea
              name="prompt"
              placeholder={t('onboarding.assistant.placeholder')}
              className={cn('ai-textarea', { loading: isAudioLoading, expanded: prompt.length > 0 })}
              value={prompt}
              onChange={(e) => handleSetPrompt(e.target.value)}
              maxLength={MAX_PROMPT_LENGTH}
            />
            <div className={cn('prompt-length', { hidden: prompt.length === 0 })}>
              <span>{prompt.length}/{MAX_PROMPT_LENGTH}</span>
            </div>
          </>
        )}

        <AudioRecorder
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          onFinishRecording={handleSendAudio}
          showLoader={isAudioLoading}
          setError={setError}
        />
      </div>

      {error && (
        <div className="ai-error-message">{t('onboarding.assistant.error')}</div>
      )}

      {prompt.length > 0 && (
        <button
          className="start-ai-search"
          onClick={startConversation}
          disabled={prompt.length < 3 || isAudioLoading || isRecording}
        >
          <i className="ri-bard-line text-xl" />
          <span>{t('onboarding.assistant.generateFeedBtn')}</span>
        </button>
      )}
      <div className="flex flex-col gap-5 items-center">
        <span className="text-tertiary font-semibold leading-5 text-sm uppercase">{t('onboarding.assistant.or')}</span>
        <button className="feed-builder-btn" onClick={handleFeedBuilderClick}>{t('onboarding.assistant.feedBuilderBtn')}</button>
      </div>
    </div>
  </div>
  )
}

export default AIAssistant
